/*
 * Registrierportal an der Anlage (Reiter „Benutzer“, docs/registrierportal.md):
 * Schalter je Anlage, offene Anmeldungen zum Freigeben oder Ablehnen und
 * offene Einladungen zum Widerrufen.
 *
 * Eine Einladung erzeugt hier niemand: den QR-Code zeigt nur das Bedienfeld
 * an der Anlage (Patrick, 18.10.2026). Aus dem Web heraus ginge es nur über
 * die Schnittstelle und nur für Systemadmins.
 */

import { useState } from 'preact/hooks';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { showToast } from '../components/Toaster';

interface PortalSettings {
  registrationEnabled: boolean;
  approvalRequired: boolean;
  defaultAccess: 'bedienen' | 'ansehen';
  portalConfigured?: boolean;
}

interface PortalRegistrationRow {
  id: string;
  displayName: string;
  email: string;
  phone: string | null;
  status: 'wartet_auf_email' | 'wartet_auf_freigabe' | 'abgeschlossen' | 'abgelehnt';
  access: string;
  createdAt: string;
  emailConfirmedAt: string | null;
  decidedAt: string | null;
  accountOutcome: string | null;
}

interface OpenInvitation {
  id: string;
  source: string;
  access: string;
  createdAt: string;
  expiresAt: string;
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) return (e.body as { error?: string } | null)?.error ?? `HTTP ${e.status}`;
  return e instanceof Error ? e.message : String(e);
}

function statusText(r: PortalRegistrationRow): string {
  switch (r.status) {
    case 'wartet_auf_email': return r.emailConfirmedAt ? 'Bestätigt, Abschluss offen' : 'E-Mail noch nicht bestätigt';
    case 'wartet_auf_freigabe': return 'Wartet auf Freigabe';
    case 'abgeschlossen': return 'Freigegeben';
    case 'abgelehnt': return 'Abgelehnt';
  }
}

const time = (iso: string) => new Date(iso).toLocaleString('de-AT', { dateStyle: 'short', timeStyle: 'short' });

export function PlantPortalSection({ plantId, onChanged }: { plantId: string; onChanged: () => void }) {
  const [tick, setTick] = useState(0);
  const settings = useAsync((s) => api.get<PortalSettings>(`/api/plants/${plantId}/portal-settings`, s), [plantId, tick]);
  const regs = useAsync((s) => api.get<PortalRegistrationRow[]>(`/api/plants/${plantId}/registrations`, s), [plantId, tick]);
  const invs = useAsync((s) => api.get<OpenInvitation[]>(`/api/plants/${plantId}/invitations`, s), [plantId, tick]);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = () => { setTick((t) => t + 1); onChanged(); };

  const save = async (next: PortalSettings) => {
    setBusy('settings');
    try {
      await api.put(`/api/plants/${plantId}/portal-settings`, next);
      reload();
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht gespeichert', message: errorText(e) });
    } finally {
      setBusy(null);
    }
  };

  const decide = async (r: PortalRegistrationRow, action: 'approve' | 'reject') => {
    if (action === 'reject' && !window.confirm(`Anmeldung von ${r.displayName} ablehnen?`)) return;
    setBusy(r.id);
    try {
      await api.post(`/api/plants/${plantId}/registrations/${r.id}/${action}`, {});
      showToast({ tone: 'success', title: action === 'approve' ? 'Freigegeben' : 'Abgelehnt' });
      reload();
    } catch (e) {
      showToast({ tone: 'error', title: action === 'approve' ? 'Nicht freigegeben' : 'Nicht abgelehnt', message: errorText(e) });
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (i: OpenInvitation) => {
    setBusy(i.id);
    try {
      await api.delete(`/api/plants/${plantId}/invitations/${i.id}`);
      reload();
    } catch (e) {
      showToast({ tone: 'error', title: 'Nicht widerrufen', message: errorText(e) });
    } finally {
      setBusy(null);
    }
  };

  const open = regs.status === 'ok'
    ? regs.data.filter((r) => r.status === 'wartet_auf_freigabe' || (r.status === 'wartet_auf_email' && r.emailConfirmedAt))
    : [];
  const recent = regs.status === 'ok' ? regs.data.filter((r) => !open.includes(r)).slice(0, 20) : [];

  return (
    <section class="hv-plantusers__portal">
      <h3>Selbst registrieren am Bedienfeld</h3>
      <p class="hv-plantusers__hint">
        Am Bedienfeld der Anlage zeigt „Benutzer hinzufügen“ einen QR-Code. Wer ihn scannt, registriert sich mit Namen
        und E-Mail-Adresse und bekommt danach Zugriff auf die Web-HMI dieser Anlage. Der Code gilt kurz und nur einmal.
      </p>
      {settings.status === 'ok' && settings.data.portalConfigured === false && (
        <p class="hv-plantusers__warn">Das Registrierportal ist auf dieser Instanz noch nicht eingerichtet.</p>
      )}
      {settings.status === 'ok' && (
        <div class="hv-plantusers__row">
          <label class="hv-plantusers__check">
            <input type="checkbox" checked={settings.data.registrationEnabled} disabled={busy === 'settings'}
                   onChange={(e) => void save({ ...settings.data, registrationEnabled: (e.target as HTMLInputElement).checked })} />
            Registrierung erlaubt
          </label>
          <label class="hv-plantusers__check">
            <input type="checkbox" checked={settings.data.approvalRequired} disabled={busy === 'settings'}
                   onChange={(e) => void save({ ...settings.data, approvalRequired: (e.target as HTMLInputElement).checked })} />
            Freigabe durch Installateur oder Hersteller nötig
          </label>
          <label class="hv-plantusers__check">
            Zugriff neuer Benutzer
            <select value={settings.data.defaultAccess} disabled={busy === 'settings'}
                    onChange={(e) => void save({ ...settings.data, defaultAccess: (e.target as HTMLSelectElement).value as 'bedienen' | 'ansehen' })}>
              <option value="bedienen">Bedienen</option>
              <option value="ansehen">Ansehen</option>
            </select>
          </label>
        </div>
      )}

      <h4>Offene Anmeldungen</h4>
      {regs.status === 'error' && <p class="hv-plantusers__warn">Nicht ladbar: {errorText(regs.error)}</p>}
      {regs.status === 'ok' && open.length === 0 && <p class="muted">Keine.</p>}
      {open.length > 0 && (
        <table class="hv-plantusers__table">
          <thead><tr><th>Name</th><th>E-Mail</th><th>Telefon</th><th>Seit</th><th>Stand</th><th /></tr></thead>
          <tbody>
            {open.map((r) => (
              <tr key={r.id}>
                <td>{r.displayName}</td>
                <td>{r.email}</td>
                <td>{r.phone ?? ''}</td>
                <td>{time(r.createdAt)}</td>
                <td>{statusText(r)}</td>
                <td class="hv-plantusers__actions">
                  <button type="button" class="hv-btn hv-btn--sm hv-btn--primary" disabled={busy === r.id} onClick={() => void decide(r, 'approve')}>Freigeben</button>
                  <button type="button" class="hv-btn hv-btn--sm" disabled={busy === r.id} onClick={() => void decide(r, 'reject')}>Ablehnen</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {recent.length > 0 && (
        <details>
          <summary>Letzte Anmeldungen ({recent.length})</summary>
          <table class="hv-plantusers__table">
            <thead><tr><th>Name</th><th>E-Mail</th><th>Seit</th><th>Stand</th><th /></tr></thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.id}>
                  <td>{r.displayName}</td>
                  <td>{r.email}</td>
                  <td>{time(r.createdAt)}</td>
                  <td>{statusText(r)}</td>
                  <td>{r.status === 'wartet_auf_email' && !r.emailConfirmedAt && (
                    <button type="button" class="hv-btn hv-btn--sm" disabled={busy === r.id} onClick={() => void decide(r, 'reject')}>Verwerfen</button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      <h4>Gültige QR-Codes</h4>
      {invs.status === 'ok' && invs.data.length === 0 && <p class="muted">Keiner.</p>}
      {invs.status === 'ok' && invs.data.length > 0 && (
        <table class="hv-plantusers__table">
          <thead><tr><th>Erzeugt</th><th>Von</th><th>Gültig bis</th><th /></tr></thead>
          <tbody>
            {invs.data.map((i) => (
              <tr key={i.id}>
                <td>{time(i.createdAt)}</td>
                <td>{i.source === 'panel' ? 'Bedienfeld' : 'Verwaltung'}</td>
                <td>{time(i.expiresAt)}</td>
                <td><button type="button" class="hv-btn hv-btn--sm" disabled={busy === i.id} onClick={() => void revoke(i)}>Widerrufen</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
